import { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Table, TableBody, TableCell, TableFooter, TableHead, TableHeader, TableRow } from '../ui/table';
import { AlertCircle, ChevronDown, ChevronRight, Download, Info, Loader2 } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipTrigger } from '../ui/tooltip';
import { apiService, AgentLlmCost, Customer, CustomerCostOverview, ModelLlmCost } from '../../services/api';
import { toast } from 'sonner';

interface Props {
  customer: Customer | null;
  /** Vorbelegung "Von" (YYYY-MM-DD) beim Öffnen — z.B. der auf der Kostenübersicht gewählte Zeitraum. Fallback: Monatsanfang. */
  initialFrom?: string;
  /** Vorbelegung "Bis" (YYYY-MM-DD) beim Öffnen. Fallback: heute. */
  initialTo?: string;
  onClose: () => void;
}

const today = () => new Date().toISOString().slice(0, 10);
const firstOfMonth = () => {
  const d = new Date();
  d.setDate(1);
  return d.toISOString().slice(0, 10);
};

const formatEur = (n: number | null | undefined): string => {
  if (n == null) return '–';
  return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR', minimumFractionDigits: 2, maximumFractionDigits: 4 }).format(n);
};

const formatTokens = (n: number): string => new Intl.NumberFormat('de-DE').format(n);

/** Wert-Paar einer Split-Zelle: EUR als Primärzeile, Tokens als Beleg darunter. */
interface CellValue {
  eur: number;
  tokens: number;
}

/** Eine Zeile der Kosten-Tabelle — Hauptkategorie, Agent- oder Detail-Unterzeile. */
interface BreakdownRow {
  key: string;
  label: string;
  /** Basis-/Mengen-Text unter dem Label (z.B. „42 Session(s)"). */
  basis?: string;
  /** Eingerückte Unterzeile (gedämpfte Darstellung mit Tree-Connector). */
  sub?: boolean;
  /** Doppelt eingerückte Modell-Unterzeile (dritte Ebene, unter einer Agent-Zeile). */
  deep?: boolean;
  /** Kursives „davon"-Präfix: informative Teilmenge der Elternzeile, keine Zusatzkosten. */
  subset?: boolean;
  /** Erklärtext hinter einem Info-Icon (Hover-Tooltip) — was misst diese Zeile? */
  info?: string;
  /** Agent-Unterzeile der LLM-Kategorie: blauer Akzent + Modell-Chip mit diesem Namen. */
  agentModel?: string;
  unpriced?: boolean;
  input: CellValue | null;
  cached: CellValue | null;
  output: CellValue | null;
  totalEur: number;
  /** Token-Summe als Beleg unter dem Gesamt-EUR (nur token-basierte Zeilen). */
  totalTokens?: number;
}

/** Modell-Unterzeile aus einem {@link ModelLlmCost}-Eintrag (dritte Ebene). */
function modelRow(m: ModelLlmCost, keyPrefix: string, unit: 'Session(s)' | 'Call(s)', deep: boolean): BreakdownRow {
  return {
    key: `${keyPrefix}-${m.provider}-${m.modelKey}-${m.reported ? 'r' : 'c'}`,
    label: m.modelName,
    basis: `${m.sessions} ${unit} · ${m.provider}${m.reported ? '' : ' · aktuelle Config (angenommen)'}`,
    sub: !deep,
    deep,
    unpriced: m.unpriced,
    input: { eur: m.inputCostEur, tokens: m.inputTokens },
    cached: { eur: m.cachedInputCostEur, tokens: m.cachedInputTokens },
    output: { eur: m.outputCostEur, tokens: m.outputTokens },
    totalEur: m.costEur,
    // Disjunkter Split (CA-622): Gesamt = Input (ungecacht) + Cache-Reads +
    // Cache-Writes + Output; Writes erscheinen nur im Gesamt (und im CSV).
    totalTokens: m.inputTokens + m.cachedInputTokens + (m.cacheWriteTokens ?? 0) + m.outputTokens,
  };
}

/**
 * Modell-Zeilen einer Agent-Zeile — nur wenn sie Information hinzufügen:
 * mehrere Modelle ODER das eine Ist-Modell weicht vom Agent-Chip (aktuelle
 * Config) ab. Sonst wäre die Zeile eine Wiederholung der Agent-Zeile.
 */
function agentModelRows(a: AgentLlmCost, keyPrefix: string): BreakdownRow[] {
  const byModel = a.byModel ?? [];
  const worthShowing = byModel.length > 1 || (byModel.length === 1 && byModel[0].modelKey !== a.modelKey);
  if (!worthShowing) return [];
  return byModel.map((m) => modelRow(m, keyPrefix, 'Session(s)', true));
}

/**
 * Flacht die Kategorien der Kostenübersicht in Tabellen-Zeilen ab.
 * Agent-Zeilen (llm.byAgent) erscheinen nur bei aufgeklapptem Zustand;
 * die Hookevaluierungs-Teilmenge bleibt immer sichtbar.
 */
function buildBreakdownRows(overview: CustomerCostOverview, agentsExpanded: boolean): BreakdownRow[] {
  const llm = overview.llm;
  const rows: BreakdownRow[] = [
    {
      key: 'llm',
      label: 'AI-Calls (LLM)',
      basis: llm.basis,
      unpriced: llm.unpriced,
      input: { eur: llm.inputCostEur ?? 0, tokens: llm.inputTokens ?? 0 },
      cached: { eur: llm.cachedInputCostEur ?? 0, tokens: llm.cachedInputTokens ?? 0 },
      output: { eur: llm.outputCostEur ?? 0, tokens: llm.outputTokens ?? 0 },
      totalEur: llm.costEur,
      totalTokens: (llm.inputTokens ?? 0) + (llm.cachedInputTokens ?? 0) + (llm.cacheWriteTokens ?? 0) + (llm.outputTokens ?? 0),
    },
  ];
  if (agentsExpanded) {
    for (const a of llm.byAgent ?? []) {
      rows.push({
        key: `llm-agent-${a.agentId}`,
        label: a.agentName,
        basis: `${a.sessions} Session(s)`,
        sub: true,
        agentModel: a.modelName,
        unpriced: a.unpriced,
        input: { eur: a.inputCostEur, tokens: a.inputTokens },
        cached: { eur: a.cachedInputCostEur, tokens: a.cachedInputTokens },
        output: { eur: a.outputCostEur, tokens: a.outputTokens },
        totalEur: a.costEur,
        totalTokens: a.inputTokens + a.cachedInputTokens + (a.cacheWriteTokens ?? 0) + a.outputTokens,
      });
      rows.push(...agentModelRows(a, `llm-agent-${a.agentId}-model`));
    }
  }
  rows.push({
    key: 'llm-hook',
    label: 'Background LLM Jobs — Hookevaluierung',
    basis: `${llm.hookEvaluation.evaluations} Auswertungen`,
    info: 'Post-Call-Entscheidung per LLM, ob ein bedingter Hook ausgelöst wird: Das LLM beurteilt anhand des Transkripts, ob die konfigurierte Sende-Bedingung erfüllt ist. Läuft nur für Hooks mit aktivierter Sende-Bedingung. Die Tokens sind bereits in der LLM-Zeile enthalten (reiner Ausweis).',
    sub: true,
    subset: true,
    unpriced: llm.hookEvaluation.unpriced,
    input: { eur: llm.hookEvaluation.inputCostEur, tokens: llm.hookEvaluation.inputTokens },
    cached: { eur: llm.hookEvaluation.cachedInputCostEur, tokens: llm.hookEvaluation.cachedInputTokens },
    output: { eur: llm.hookEvaluation.outputCostEur, tokens: llm.hookEvaluation.outputTokens },
    totalEur: llm.hookEvaluation.costEur,
    totalTokens: llm.hookEvaluation.tokens,
  });
  // Defensive Optionalität: ältere Backend-Responses (vor CA-622-Erweiterung)
  // tragen das Feld noch nicht — Zeile dann einfach weglassen.
  const evalCls = llm.evaluationClassification as CustomerCostOverview['llm']['evaluationClassification'] | undefined;
  if (evalCls) {
    rows.push({
      key: 'llm-evaluation',
      label: 'Background LLM Jobs — Evaluierung (Reporting)',
      basis: `${evalCls.evaluations} Auswertungen`,
      info: 'Qualitative Klassifikation fürs Reporting: pro Auswertungs-Kriterium ein LLM-Call nach Sitzungsende. Die Tokens sind bereits in der LLM-Zeile enthalten (reiner Ausweis).',
      sub: true,
      subset: true,
      unpriced: evalCls.unpriced,
      input: { eur: evalCls.inputCostEur, tokens: evalCls.inputTokens },
      cached: { eur: evalCls.cachedInputCostEur, tokens: evalCls.cachedInputTokens },
      output: { eur: evalCls.outputCostEur, tokens: evalCls.outputTokens },
      totalEur: evalCls.costEur,
      totalTokens: evalCls.tokens,
    });
  }
  // Session-gebundene Hintergrund-Anteile (CA-622): additiv in llm.costEur
  // eingerechnet (nicht Teil der Session-Totale) — nach der Addition echte
  // „davon"-Teilmengen der LLM-Zeile. Defensive Optionalität wie oben.
  const summaryGen = llm.summaryGeneration as CustomerCostOverview['llm']['summaryGeneration'] | undefined;
  if (summaryGen) {
    rows.push({
      key: 'llm-summary-generation',
      label: 'Background LLM Jobs — Zusammenfassung',
      basis: `${summaryGen.calls} Call(s)`,
      info: 'LLM-Call der Gesprächszusammenfassung nach Sitzungsende. Nicht Teil der Session-Totale — wird zusätzlich (additiv) in die LLM-Zeile eingerechnet.',
      sub: true,
      subset: true,
      unpriced: summaryGen.unpriced,
      input: { eur: summaryGen.inputCostEur, tokens: summaryGen.inputTokens },
      cached: { eur: summaryGen.cachedInputCostEur, tokens: summaryGen.cachedInputTokens },
      output: { eur: summaryGen.outputCostEur, tokens: summaryGen.outputTokens },
      totalEur: summaryGen.costEur,
      totalTokens: summaryGen.tokens,
    });
    for (const m of summaryGen.byModel ?? []) {
      rows.push(modelRow(m, 'llm-summary-generation-model', 'Call(s)', true));
    }
  }
  const hookContent = llm.hookContent as CustomerCostOverview['llm']['hookContent'] | undefined;
  if (hookContent) {
    rows.push({
      key: 'llm-hook-content',
      label: 'Background LLM Jobs — Hook-Inhalte (E-Mail/SMS)',
      basis: `${hookContent.calls} Call(s)`,
      info: 'Generierung des E-Mail-/SMS-Textes für Hooks mit Inhaltsmodus „Prompt" — läuft bei jeder Ausführung solcher Hooks, auch ohne Sende-Bedingung. Nicht Teil der Session-Totale — additiv in die LLM-Zeile eingerechnet.',
      sub: true,
      subset: true,
      unpriced: hookContent.unpriced,
      input: { eur: hookContent.inputCostEur, tokens: hookContent.inputTokens },
      cached: { eur: hookContent.cachedInputCostEur, tokens: hookContent.cachedInputTokens },
      output: { eur: hookContent.outputCostEur, tokens: hookContent.outputTokens },
      totalEur: hookContent.costEur,
      totalTokens: hookContent.tokens,
    });
    for (const m of hookContent.byModel ?? []) {
      rows.push(modelRow(m, 'llm-hook-content-model', 'Call(s)', true));
    }
  }
  rows.push(
    { key: 'stt', label: 'Speech-to-Text', basis: overview.stt.basis, unpriced: overview.stt.unpriced, input: null, cached: null, output: null, totalEur: overview.stt.costEur },
    { key: 'tts', label: 'Text-to-Speech', basis: overview.tts.basis, unpriced: overview.tts.unpriced, input: null, cached: null, output: null, totalEur: overview.tts.costEur },
    { key: 'rag', label: 'RAG (Embedding/Retrieval)', basis: overview.rag.basis, unpriced: overview.rag.unpriced, input: null, cached: null, output: null, totalEur: overview.rag.costEur },
  );
  const it = overview.internalTools;
  rows.push({
    key: 'internal-tools',
    label: 'KI-Assistent / Einrichtung',
    basis: it.basis,
    unpriced: it.unpriced,
    input: it.calls > 0 ? { eur: it.inputCostEur, tokens: it.inputTokens } : null,
    cached: it.calls > 0 ? { eur: it.cachedInputCostEur, tokens: it.cachedInputTokens } : null,
    output: it.calls > 0 ? { eur: it.outputCostEur, tokens: it.outputTokens } : null,
    totalEur: it.costEur,
    totalTokens: it.calls > 0 ? it.inputTokens + it.cachedInputTokens + (it.cacheWriteTokens ?? 0) + it.outputTokens : undefined,
  });
  for (const s of it.bySource) {
    rows.push({
      key: `internal-tools-${s.source}`,
      label: s.label,
      basis: `${s.calls} Call(s)`,
      sub: true,
      input: null,
      cached: null,
      output: null,
      totalEur: s.costEur,
    });
  }
  for (const m of it.byModel ?? []) {
    rows.push(modelRow(m, 'internal-tools-model', 'Call(s)', false));
  }
  const qa = overview.qa;
  rows.push(
    { key: 'qa', label: 'QA/Evaluation', basis: qa.basis, unpriced: qa.unpriced, input: null, cached: null, output: null, totalEur: qa.costEur },
    { key: 'qa-eval-service', label: 'Eval-Service', basis: `${qa.runCount} Run(s)`, sub: true, input: null, cached: null, output: null, totalEur: qa.evalServiceCostEur },
    {
      key: 'qa-bot-llm',
      label: 'Bot-LLM (Evaluation-Sessions)',
      sub: true,
      input: { eur: qa.botLlmInputCostEur, tokens: qa.botLlmInputTokens },
      cached: { eur: qa.botLlmCachedInputCostEur, tokens: qa.botLlmCachedInputTokens },
      output: { eur: qa.botLlmOutputCostEur, tokens: qa.botLlmOutputTokens },
      totalEur: qa.botLlmCostEur,
      totalTokens: qa.botLlmInputTokens + qa.botLlmCachedInputTokens + (qa.botLlmCacheWriteTokens ?? 0) + qa.botLlmOutputTokens,
    },
  );
  for (const a of qa.botLlmByAgent ?? []) {
    rows.push({
      key: `qa-bot-llm-agent-${a.agentId}`,
      label: a.agentName,
      basis: `${a.sessions} Session(s)`,
      deep: true,
      agentModel: a.modelName,
      unpriced: a.unpriced,
      input: { eur: a.inputCostEur, tokens: a.inputTokens },
      cached: { eur: a.cachedInputCostEur, tokens: a.cachedInputTokens },
      output: { eur: a.outputCostEur, tokens: a.outputTokens },
      totalEur: a.costEur,
      totalTokens: a.inputTokens + a.cachedInputTokens + (a.cacheWriteTokens ?? 0) + a.outputTokens,
    });
    rows.push(...agentModelRows(a, `qa-bot-llm-agent-${a.agentId}-model`));
  }
  return rows;
}

/** Getönter Zellen-Hintergrund der Cached-Spalte (Teilmenge von Input). */
const CACHED_CELL = 'bg-amber-50/60';

export function CustomerCostOverviewDialog({ customer, initialFrom, initialTo, onClose }: Props) {
  const [from, setFrom] = useState<string>(initialFrom ?? firstOfMonth());
  const [to, setTo] = useState<string>(initialTo ?? today());
  const [loading, setLoading] = useState(false);
  const [overview, setOverview] = useState<CustomerCostOverview | null>(null);
  const [error, setError] = useState<string | null>(null);

  const open = !!customer;

  const load = async (f: string, t: string) => {
    if (!customer) return;
    setLoading(true);
    setError(null);
    try {
      const fromIso = `${f}T00:00:00Z`;
      const toIso = `${t}T23:59:59Z`;
      const data = await apiService.getCustomerCostOverview(customer.id, fromIso, toIso);
      setOverview(data);
    } catch (err: any) {
      setError(err?.message || 'Fehler beim Laden der Kostenübersicht');
      setOverview(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      // load() gets the range explicitly — state updates land erst im nächsten Render
      const f = initialFrom ?? firstOfMonth();
      const t = initialTo ?? today();
      setFrom(f);
      setTo(t);
      setOverview(null);
      setError(null);
      load(f, t);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, customer?.id]);

  const handleApply = () => {
    // same-day ranges are valid: the fetch spans T00:00:00Z–T23:59:59Z
    if (!from || !to || from > to) {
      toast.error('Ungültiger Zeitraum: "Von" darf nicht nach "Bis" liegen.');
      return;
    }
    load(from, to);
  };

  const handleExportCsv = () => {
    if (!overview) return;
    const rows = [
      ['Kategorie', 'Kosten (EUR)', 'Basis'],
      ['LLM (AI-Calls)', overview.llm.costEur.toFixed(4), overview.llm.basis],
      ['LLM Input', overview.llm.inputCostEur.toFixed(4), `${overview.llm.inputTokens} Tokens`],
      ['LLM Cache-Reads', (overview.llm.cachedInputCostEur ?? 0).toFixed(4), `${overview.llm.cachedInputTokens ?? 0} Tokens`],
      ['LLM Cache-Writes', (overview.llm.cacheWriteCostEur ?? 0).toFixed(4), `${overview.llm.cacheWriteTokens ?? 0} Tokens`],
      ['LLM Output', overview.llm.outputCostEur.toFixed(4), `${overview.llm.outputTokens} Tokens`],
      ...(overview.llm.byAgent ?? []).flatMap((a) => [
        [
          `LLM Agent: ${a.agentName} (${a.modelName})`,
          a.costEur.toFixed(4),
          `${a.sessions} Session(s), ${a.inputTokens} Input, ${a.cachedInputTokens} Cache-Read, ${a.cacheWriteTokens ?? 0} Cache-Write, ${a.outputTokens} Output Tokens`,
        ],
        ...(a.byModel ?? []).map((m) => [
          `LLM Agent: ${a.agentName} — Modell ${m.provider}/${m.modelKey}${m.reported ? '' : ' (aktuelle Config angenommen)'}`,
          m.costEur.toFixed(4),
          `${m.sessions} Session(s), ${m.inputTokens} Input, ${m.cachedInputTokens} Cache-Read, ${m.cacheWriteTokens ?? 0} Cache-Write, ${m.outputTokens} Output Tokens`,
        ]),
      ]),
      ['LLM davon Background Jobs (Hookevaluierung)', overview.llm.hookEvaluation.costEur.toFixed(4), `${overview.llm.hookEvaluation.evaluations} Auswertungen`],
      ['Background Jobs Input', overview.llm.hookEvaluation.inputCostEur.toFixed(4), `${overview.llm.hookEvaluation.inputTokens} Tokens`],
      ['Background Jobs Cache-Reads', overview.llm.hookEvaluation.cachedInputCostEur.toFixed(4), `${overview.llm.hookEvaluation.cachedInputTokens} Tokens`],
      ['Background Jobs Cache-Writes', (overview.llm.hookEvaluation.cacheWriteCostEur ?? 0).toFixed(4), `${overview.llm.hookEvaluation.cacheWriteTokens ?? 0} Tokens`],
      ['Background Jobs Output', overview.llm.hookEvaluation.outputCostEur.toFixed(4), `${overview.llm.hookEvaluation.outputTokens} Tokens`],
      ...(overview.llm.evaluationClassification
        ? [
            ['LLM davon Background Jobs (Evaluierung/Reporting)', overview.llm.evaluationClassification.costEur.toFixed(4), `${overview.llm.evaluationClassification.evaluations} Auswertungen`],
            ['Evaluierung Input', overview.llm.evaluationClassification.inputCostEur.toFixed(4), `${overview.llm.evaluationClassification.inputTokens} Tokens`],
            ['Evaluierung Cache-Reads', overview.llm.evaluationClassification.cachedInputCostEur.toFixed(4), `${overview.llm.evaluationClassification.cachedInputTokens} Tokens`],
            ['Evaluierung Cache-Writes', (overview.llm.evaluationClassification.cacheWriteCostEur ?? 0).toFixed(4), `${overview.llm.evaluationClassification.cacheWriteTokens ?? 0} Tokens`],
            ['Evaluierung Output', overview.llm.evaluationClassification.outputCostEur.toFixed(4), `${overview.llm.evaluationClassification.outputTokens} Tokens`],
          ]
        : []),
      ...(overview.llm.summaryGeneration
        ? [
            ['LLM davon Background Jobs (Zusammenfassung)', overview.llm.summaryGeneration.costEur.toFixed(4), `${overview.llm.summaryGeneration.calls} Call(s)`],
            ['Zusammenfassung Input', overview.llm.summaryGeneration.inputCostEur.toFixed(4), `${overview.llm.summaryGeneration.inputTokens} Tokens`],
            ['Zusammenfassung Cache-Reads', overview.llm.summaryGeneration.cachedInputCostEur.toFixed(4), `${overview.llm.summaryGeneration.cachedInputTokens} Tokens`],
            ['Zusammenfassung Cache-Writes', (overview.llm.summaryGeneration.cacheWriteCostEur ?? 0).toFixed(4), `${overview.llm.summaryGeneration.cacheWriteTokens ?? 0} Tokens`],
            ['Zusammenfassung Output', overview.llm.summaryGeneration.outputCostEur.toFixed(4), `${overview.llm.summaryGeneration.outputTokens} Tokens`],
            ...(overview.llm.summaryGeneration.byModel ?? []).map((m) => [
              `Zusammenfassung Modell: ${m.provider}/${m.modelKey}`,
              m.costEur.toFixed(4),
              `${m.sessions} Call(s), ${m.inputTokens} Input, ${m.cachedInputTokens} Cache-Read, ${m.cacheWriteTokens ?? 0} Cache-Write, ${m.outputTokens} Output Tokens`,
            ]),
          ]
        : []),
      ...(overview.llm.hookContent
        ? [
            ['LLM davon Background Jobs (Hook-Inhalte E-Mail/SMS)', overview.llm.hookContent.costEur.toFixed(4), `${overview.llm.hookContent.calls} Call(s)`],
            ['Hook-Inhalte Input', overview.llm.hookContent.inputCostEur.toFixed(4), `${overview.llm.hookContent.inputTokens} Tokens`],
            ['Hook-Inhalte Cache-Reads', overview.llm.hookContent.cachedInputCostEur.toFixed(4), `${overview.llm.hookContent.cachedInputTokens} Tokens`],
            ['Hook-Inhalte Cache-Writes', (overview.llm.hookContent.cacheWriteCostEur ?? 0).toFixed(4), `${overview.llm.hookContent.cacheWriteTokens ?? 0} Tokens`],
            ['Hook-Inhalte Output', overview.llm.hookContent.outputCostEur.toFixed(4), `${overview.llm.hookContent.outputTokens} Tokens`],
            ...(overview.llm.hookContent.byModel ?? []).map((m) => [
              `Hook-Inhalte Modell: ${m.provider}/${m.modelKey}`,
              m.costEur.toFixed(4),
              `${m.sessions} Call(s), ${m.inputTokens} Input, ${m.cachedInputTokens} Cache-Read, ${m.cacheWriteTokens ?? 0} Cache-Write, ${m.outputTokens} Output Tokens`,
            ]),
          ]
        : []),
      ['Speech-to-Text', overview.stt.costEur.toFixed(4), overview.stt.basis],
      ['Text-to-Speech', overview.tts.costEur.toFixed(4), overview.tts.basis],
      ['RAG', overview.rag.costEur.toFixed(4), overview.rag.basis],
      ['KI-Assistent / Einrichtung', overview.internalTools.costEur.toFixed(4), overview.internalTools.basis],
      ['KI-Assistent Input', overview.internalTools.inputCostEur.toFixed(4), `${overview.internalTools.inputTokens} Tokens`],
      ['KI-Assistent Cache-Reads', overview.internalTools.cachedInputCostEur.toFixed(4), `${overview.internalTools.cachedInputTokens} Tokens`],
      ['KI-Assistent Cache-Writes', (overview.internalTools.cacheWriteCostEur ?? 0).toFixed(4), `${overview.internalTools.cacheWriteTokens ?? 0} Tokens`],
      ['KI-Assistent Output', overview.internalTools.outputCostEur.toFixed(4), `${overview.internalTools.outputTokens} Tokens`],
      ...(overview.internalTools.byModel ?? []).map((m) => [
        `KI-Assistent Modell: ${m.provider}/${m.modelKey}`,
        m.costEur.toFixed(4),
        `${m.sessions} Call(s), ${m.inputTokens} Input, ${m.cachedInputTokens} Cache-Read, ${m.cacheWriteTokens ?? 0} Cache-Write, ${m.outputTokens} Output Tokens`,
      ]),
      ['QA/Evaluation', overview.qa.costEur.toFixed(4), overview.qa.basis],
      ['QA Eval-Service', overview.qa.evalServiceCostEur.toFixed(4), `${overview.qa.runCount} Run(s)`],
      ['QA Bot-LLM', overview.qa.botLlmCostEur.toFixed(4), ''],
      ['QA Bot-LLM Input', overview.qa.botLlmInputCostEur.toFixed(4), `${overview.qa.botLlmInputTokens} Tokens`],
      ['QA Bot-LLM Cache-Reads', overview.qa.botLlmCachedInputCostEur.toFixed(4), `${overview.qa.botLlmCachedInputTokens} Tokens`],
      ['QA Bot-LLM Cache-Writes', (overview.qa.botLlmCacheWriteCostEur ?? 0).toFixed(4), `${overview.qa.botLlmCacheWriteTokens ?? 0} Tokens`],
      ['QA Bot-LLM Output', overview.qa.botLlmOutputCostEur.toFixed(4), `${overview.qa.botLlmOutputTokens} Tokens`],
      ...(overview.qa.botLlmByAgent ?? []).flatMap((a) => [
        [
          `QA Bot-LLM Agent: ${a.agentName} (${a.modelName})`,
          a.costEur.toFixed(4),
          `${a.sessions} Session(s), ${a.inputTokens} Input, ${a.cachedInputTokens} Cache-Read, ${a.cacheWriteTokens ?? 0} Cache-Write, ${a.outputTokens} Output Tokens`,
        ],
        ...(a.byModel ?? []).map((m) => [
          `QA Bot-LLM Agent: ${a.agentName} — Modell ${m.provider}/${m.modelKey}${m.reported ? '' : ' (aktuelle Config angenommen)'}`,
          m.costEur.toFixed(4),
          `${m.sessions} Session(s), ${m.inputTokens} Input, ${m.cachedInputTokens} Cache-Read, ${m.cacheWriteTokens ?? 0} Cache-Write, ${m.outputTokens} Output Tokens`,
        ]),
      ]),
      ['', '', ''],
      ['Total Kosten', overview.totalCostEur.toFixed(4), ''],
      ['Geschätzter Umsatz', overview.estimatedRevenueEur != null ? overview.estimatedRevenueEur.toFixed(4) : '–', ''],
      ['Geschätzte Marge', overview.estimatedMarginEur != null ? overview.estimatedMarginEur.toFixed(4) : '–', ''],
      ['Sessions im Zeitraum', String(overview.sessionCount), ''],
    ];
    const csv = rows.map((r) => r.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `kosten-${overview.customerName.replace(/[^a-z0-9]/gi, '_')}-${from}-${to}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const dataAvailableFromWarning = overview?.dataAvailableFrom && new Date(overview.dataAvailableFrom) > new Date(`${from}T00:00:00Z`)
    ? `Daten ab ${new Date(overview.dataAvailableFrom).toLocaleDateString('de-DE')} verfügbar — frühere Tage sind in der Summe nicht enthalten.`
    : null;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-4xl sm:max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Kostenübersicht — {customer?.name}</DialogTitle>
          <DialogDescription>
            Geschätzte Kosten für LLM, Speech-to-Text, Text-to-Speech, RAG und QA/Evaluation sowie Umsatz und Marge im gewählten Zeitraum.
            Werte sind Schätzungen basierend auf den pro Modell/Voice konfigurierten Preisen.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 items-end">
            <div className="space-y-1">
              <Label htmlFor="cost-from">Von</Label>
              <Input id="cost-from" type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
            </div>
            <div className="space-y-1">
              <Label htmlFor="cost-to">Bis</Label>
              <Input id="cost-to" type="date" value={to} onChange={(e) => setTo(e.target.value)} />
            </div>
            <Button onClick={handleApply} disabled={loading} className="sm:justify-self-start sm:px-8">
              {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Anwenden
            </Button>
          </div>

          {dataAvailableFromWarning && (
            <div className="flex items-start gap-2 p-3 rounded-md bg-amber-50 border border-amber-200 text-amber-800 text-sm">
              <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
              <span>{dataAvailableFromWarning}</span>
            </div>
          )}

          {error && (
            <div className="flex items-start gap-2 p-3 rounded-md bg-red-50 border border-red-200 text-red-800 text-sm">
              <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {overview && !loading && (
            <>
              <Card>
                <CardContent className="p-0">
                  <CostBreakdownTable overview={overview} />
                  <div className="flex flex-wrap gap-x-6 gap-y-1 px-3 py-2 text-[11px] text-muted-foreground border-t">
                    <span>Je Zelle: EUR-Betrag, darunter die zugrunde liegenden Tokens. Input (ungecacht), Cache-Reads und Output sind disjunkte Kostenposten — das Gesamt ist ihre Summe (inkl. etwaiger Cache-Writes, siehe CSV). „davon"-Zeilen sind informative Teilmengen der Elternzeile.</span>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="pt-6 space-y-3">
                  <div className="flex justify-between">
                    <span>Geschätzter Umsatz</span>
                    <span>{formatEur(overview.estimatedRevenueEur)}</span>
                  </div>
                  <div className="border-t pt-3 flex justify-between font-semibold">
                    <span>Geschätzte Marge</span>
                    <span className={overview.estimatedMarginEur != null && overview.estimatedMarginEur < 0 ? 'text-red-600' : 'text-green-700'}>
                      {formatEur(overview.estimatedMarginEur)}
                    </span>
                  </div>
                </CardContent>
              </Card>

              <div className="flex items-center justify-between text-sm text-gray-600">
                <span>{overview.sessionCount} geschlossene Session(s) im Zeitraum</span>
                <Button variant="outline" size="sm" onClick={handleExportCsv}>
                  <Download className="w-4 h-4 mr-2" />
                  CSV Export
                </Button>
              </div>
            </>
          )}

          {loading && !overview && (
            <div className="flex items-center justify-center p-8 text-gray-500">
              <Loader2 className="w-5 h-5 animate-spin mr-2" />
              Lade Kostenübersicht...
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Kosten-Aufschlüsselung als kompakte 5-Spalten-Tabelle: Kategorie | Input |
 * davon Cached | Output | Gesamt €. Jede Split-Zelle zeigt EUR primär und die
 * Tokens als Beleg darunter. Die Agent-Untergliederung der LLM-Kategorie ist
 * default zugeklappt (Chip-Toggle in der LLM-Zeile).
 */
function CostBreakdownTable({ overview }: { overview: CustomerCostOverview }) {
  const [agentsExpanded, setAgentsExpanded] = useState(false);
  const rows = buildBreakdownRows(overview, agentsExpanded);
  const agentCount = (overview.llm.byAgent ?? []).length;
  return (
    <Table>
      <TableHeader>
        <TableRow className="hover:bg-transparent">
          <TableHead className="pl-3">Kategorie</TableHead>
          <NumHead label="Input" />
          <NumHead label="Cache (Reads)" cached />
          <NumHead label="Output" />
          <NumHead label="Gesamt €" className="pr-3" />
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map((row) => (
          <BreakdownTableRow
            key={row.key}
            row={row}
            agentToggle={
              row.key === 'llm' && agentCount > 0
                ? { count: agentCount, expanded: agentsExpanded, onToggle: () => setAgentsExpanded((v) => !v) }
                : undefined
            }
          />
        ))}
      </TableBody>
      <TableFooter>
        <TableRow className="hover:bg-transparent">
          <TableCell className="pl-3 font-semibold">Gesamtkosten</TableCell>
          <TableCell colSpan={3} />
          <TableCell className="text-right pr-3 font-semibold tabular-nums">{formatEur(overview.totalCostEur)}</TableCell>
        </TableRow>
      </TableFooter>
    </Table>
  );
}

function NumHead({ label, cached, className }: { label: string; cached?: boolean; className?: string }) {
  return (
    <TableHead className={`text-right align-bottom ${cached ? `text-amber-700 ${CACHED_CELL}` : ''} ${className ?? ''}`}>
      {label}
      <span className="block text-[9px] font-normal text-muted-foreground/70">EUR · Tokens</span>
    </TableHead>
  );
}

/** Toggle-Chip in der LLM-Zeile: klappt die Agent-Unterzeilen auf/zu. */
interface AgentToggle {
  count: number;
  expanded: boolean;
  onToggle: () => void;
}

function BreakdownTableRow({ row, agentToggle }: { row: BreakdownRow; agentToggle?: AgentToggle }) {
  const indented = row.sub || row.deep;
  // Einrückungs-Wrapper: Agent-Zeilen ohne Connector (blauer Akzent), Detail-
  // Zeilen mit Tree-Connector — dritte Ebene (deep) eine Stufe weiter rechts.
  const wrapperClass = row.agentModel
    ? row.deep ? 'pl-5' : 'pl-2'
    : row.deep
      ? 'relative pl-9 before:absolute before:left-5 before:top-0 before:h-2.5 before:w-px before:bg-border after:absolute after:left-5 after:top-2.5 after:h-px after:w-2 after:bg-border'
      : indented
        ? 'relative pl-5 before:absolute before:left-1.5 before:top-0 before:h-2.5 before:w-px before:bg-border after:absolute after:left-1.5 after:top-2.5 after:h-px after:w-2 after:bg-border'
        : undefined;
  return (
    <TableRow className={indented ? 'bg-muted/20' : undefined}>
      <TableCell className={`pl-3 whitespace-normal align-top ${row.agentModel ? 'border-l-2 border-l-blue-400' : ''}`}>
        <div className={wrapperClass}>
          <span className={row.agentModel ? 'text-xs font-medium' : indented ? 'text-xs text-muted-foreground' : 'font-medium'}>
            {row.subset && <span className="italic text-muted-foreground/70 mr-1">davon</span>}
            {row.label}
          </span>
          {row.info && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Info className="w-3 h-3 ml-1 inline-block align-middle text-muted-foreground/60 hover:text-muted-foreground cursor-help" />
              </TooltipTrigger>
              <TooltipContent className="max-w-xs text-xs leading-snug">{row.info}</TooltipContent>
            </Tooltip>
          )}
          {row.agentModel && (
            <Badge variant="secondary" className="bg-blue-100 text-blue-800 ml-2 text-[10px] align-middle">{row.agentModel}</Badge>
          )}
          {row.unpriced && (
            <Badge variant="secondary" className="bg-amber-100 text-amber-800 ml-2 text-[10px] align-middle">Pricing fehlt</Badge>
          )}
          {agentToggle && (
            <button
              type="button"
              onClick={agentToggle.onToggle}
              className="ml-2 inline-flex items-center gap-1 rounded-full border border-blue-200 bg-blue-50 px-2 py-0.5 text-[10px] font-medium text-blue-700 hover:bg-blue-100 align-middle"
            >
              {agentToggle.expanded ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
              {agentToggle.count} Agent(s) {agentToggle.expanded ? 'ausblenden' : 'einblenden'}
            </button>
          )}
          {row.basis && <div className="text-[11px] text-muted-foreground/80 mt-0.5">{row.basis}</div>}
        </div>
      </TableCell>
      <SplitCell value={row.input} sub={indented} />
      <SplitCell value={row.cached} sub={indented} cached />
      <SplitCell value={row.output} sub={indented} />
      <TableCell className="text-right pr-3 align-top tabular-nums">
        <div className="flex flex-col items-end">
          <span className={`${indented ? 'text-xs text-muted-foreground' : 'font-medium'}`}>{formatEur(row.totalEur)}</span>
          {row.totalTokens != null && (
            <span className="text-[10px] text-muted-foreground/70">{formatTokens(row.totalTokens)} Tok.</span>
          )}
        </div>
      </TableCell>
    </TableRow>
  );
}

/**
 * Numerische Split-Zelle: EUR primär, Tokens darunter; `value === null` ⇒
 * gedämpfter Strich (nicht token-basierte Kategorie). Null-Werte (0 Tokens,
 * 0 €) werden ausgegraut statt versteckt.
 */
function SplitCell({ value, sub, cached }: { value: CellValue | null; sub?: boolean; cached?: boolean }) {
  const base = `text-right align-top tabular-nums ${cached ? CACHED_CELL : ''}`;
  if (!value) {
    return (
      <TableCell className={base}>
        <span className="text-muted-foreground/40">–</span>
      </TableCell>
    );
  }
  const isZero = value.eur === 0 && value.tokens === 0;
  const eurClass = isZero
    ? 'text-muted-foreground/40'
    : cached
      ? 'text-amber-700'
      : sub
        ? 'text-xs text-muted-foreground'
        : '';
  return (
    <TableCell className={base}>
      <div className="flex flex-col items-end">
        <span className={`${sub ? 'text-xs' : ''} ${eurClass}`}>{formatEur(value.eur)}</span>
        <span className={`text-[10px] ${isZero ? 'text-muted-foreground/40' : cached ? 'text-amber-600/70' : 'text-muted-foreground/70'}`}>
          {formatTokens(value.tokens)} Tok.
        </span>
      </div>
    </TableCell>
  );
}
